import { jsx } from '@amazon/vinyl-tsx'
import { data, type MutableValue } from '@amazon/vinyl-observable'
import { Modal } from '@/components/Modal'
import { Icon } from '@/components/Icon'
import { ctx } from '@/util/context'
import { toast } from '@/components/toast'
import { navigateTo } from '@/router/router'
import { createPlaylist, type PlaylistVisibility } from '@/playlistdetail/playlistDetailService'
import { getString } from '@/library/i18n/bundle.generated'

const MAX_TITLE_LENGTH = 100

export interface CreatePlaylistModalProps {
    readonly visible: MutableValue<boolean>
}

export function CreatePlaylistModal({ visible }: CreatePlaylistModalProps) {
    const { errorHandler } = ctx('CreatePlaylistModal')

    const title = data('')
    const visibility = data<PlaylistVisibility>('PRIVATE')
    const saving = data(false)

    const close = () => {
        if (saving.value) return
        title.value = ''
        visibility.value = 'PRIVATE'
        visible.value = false
    }

    const saveDisabled$ = title.map((t) => t.trim().length === 0 || t.length > MAX_TITLE_LENGTH)

    function onSave() {
        const trimmed = title.value.trim()
        if (!trimmed || saving.value) return
        saving.value = true
        createPlaylist({ title: trimmed, visibility: visibility.value })
            .then((created) => {
                saving.value = false
                if (!created) {
                    toast(getString('dmusic_create_playlist_modal_create_playlist_failed').value)
                    return
                }
                visible.value = false
                title.value = ''
                visibility.value = 'PRIVATE'
                navigateTo(`/playlists/${created.id}`)
            })
            .catch((err) => {
                saving.value = false
                toast(getString('dmusic_create_playlist_modal_create_playlist_failed').value)
                errorHandler(err)
            })
    }

    return (
        <Modal
            className="createPlaylistModal"
            visible={visible}
            onClose={() => close()}
            aria-label={getString('dmusic_create_playlist_modal_title').value}
        >
            <h2 className="createPlaylistModalTitle">
                {getString('dmusic_create_playlist_modal_title')}
            </h2>
            <label className="createPlaylistModalLabel">
                {getString('dmusic_create_playlist_modal_playlist_name')}
            </label>
            <input
                className="createPlaylistModalInput"
                type="text"
                maxLength={MAX_TITLE_LENGTH}
                placeholder={getString('dmusic_create_playlist_modal_playlist_name_placeholder')}
                value=""
                oninput={(event) => {
                    title.value = (event.target as HTMLInputElement).value
                }}
                onkeydown={(event: KeyboardEvent) => {
                    if (event.key === 'Enter' && !saveDisabled$.value) {
                        event.preventDefault()
                        onSave()
                    }
                }}
                onConnect={(input) => {
                    // Focus on the next frame so the modal's own focus
                    // trap (which targets the close button first) has
                    // settled before we steal focus to the input.
                    requestAnimationFrame(() => (input as HTMLInputElement).focus())
                }}
            />
            <div className="createPlaylistModalPrivacy">
                <label className="createPlaylistModalLabel">
                    {getString('dmusic_create_playlist_modal_privacy')}
                </label>
                <div className="createPlaylistModalSegmented" role="radiogroup">
                    <button
                        type="button"
                        role="radio"
                        className="createPlaylistModalSegment"
                        classList={[visibility.map((v) => (v === 'PRIVATE' ? 'selected' : null))]}
                        aria-checked={visibility.map((v) => v === 'PRIVATE')}
                        onclick={() => (visibility.value = 'PRIVATE')}
                    >
                        <Icon icon="visibilityOff" />
                        <span>Private</span>
                    </button>
                    <button
                        type="button"
                        role="radio"
                        className="createPlaylistModalSegment"
                        classList={[visibility.map((v) => (v === 'PUBLIC' ? 'selected' : null))]}
                        aria-checked={visibility.map((v) => v === 'PUBLIC')}
                        onclick={() => (visibility.value = 'PUBLIC')}
                    >
                        <Icon icon="globe" />
                        <span>{getString('dmusic_create_playlist_modal_playlist_public')}</span>
                    </button>
                </div>
            </div>
            <div className="createPlaylistModalActions">
                <button
                    className="textButton clear createPlaylistModalCancelBtn"
                    onclick={() => close()}
                    disabled={saving}
                >
                    {getString('dmusic_create_playlist_modal_cancel')}
                </button>
                <button
                    className="textButton createPlaylistModalSaveBtn"
                    onclick={onSave}
                    disabled={saveDisabled$}
                >
                    {getString('dmusic_create_playlist_modal_playlist_save')}
                </button>
            </div>
        </Modal>
    )
}
